import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { SUBJECT_META } from '../../data/standards'
import { useStandards } from '../../providers/StandardsProvider'
import { useAuth } from '../../providers/AuthProvider'
import { KnowledgeUnit, PASS_META, PassId, Provider, ROLE_CAPS } from '../../types'
import { useKUs } from '../../hooks/useKUs'
import { useRuns } from '../../hooks/useRuns'
import { usePrompts } from '../../hooks/usePrompts'
import PassCard from './PassCard'

interface Props {
  standardId: string
  onSelectKU: (ko: KnowledgeUnit) => void
  onNavigateToRoot?: () => void
}

export default function StandardDetailPanel({ standardId, onSelectKU, onNavigateToRoot }: Props) {
  const { user, profile } = useAuth()
  const caps = ROLE_CAPS[profile?.role || 'teacher']

  const { findStandard } = useStandards()
  const stdMeta = findStandard(standardId)
  const { fetchKUs, upsertVote } = useKUs()
  const { executeRun } = useRuns(user?.id || '')
  const { getPrompt } = usePrompts()

  const [kusByPass, setKosByPass] = useState<Record<PassId, KnowledgeUnit[]>>({
    pass1: [],
    pass2: [],
    pass3: [],
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadKUs = useCallback(async () => {
    setLoading(true)
    const all = await fetchKUs(standardId)
    setKosByPass({
      pass1: all.filter((k) => k.pass_id === 'pass1'),
      pass2: all.filter((k) => k.pass_id === 'pass2'),
      pass3: all.filter((k) => k.pass_id === 'pass3'),
    })
    setLoading(false)
  }, [standardId, fetchKUs])

  useEffect(() => {
    loadKUs()
  }, [loadKUs])

  const handleGenerate = useCallback(
    async (passId: PassId, provider: Provider, model: string) => {
      if (!stdMeta) return
      setError(null)

      const pass = PASS_META.find((p) => p.id === passId)!
      let sourceDoc: string | undefined

      if (pass.hasGenerate) {
        const genPrompt = getPrompt(passId, 'generate')
        if (!genPrompt) {
          setError('Generate prompt not found')
          return
        }
        const genResult = await executeRun({
          standardId,
          standardText: stdMeta.std.text,
          subject: stdMeta.subject,
          grade: stdMeta.grade,
          passId,
          provider,
          model,
          promptText: genPrompt.text,
          slot: 'generate',
          promptLabel: genPrompt.label,
          kuCount: 5,
        })
        if (!genResult) {
          setError('Document generation failed')
          return
        }
        sourceDoc = genResult.run.source_doc || undefined
      }

      const extractPrompt = getPrompt(passId, 'extract')
      if (!extractPrompt) {
        setError('Extract prompt not found')
        return
      }
      const result = await executeRun({
        standardId,
        standardText: stdMeta.std.text,
        subject: stdMeta.subject,
        grade: stdMeta.grade,
        passId,
        provider,
        model,
        promptText: extractPrompt.text,
        slot: 'extract',
        promptLabel: extractPrompt.label,
        sourceDoc,
        kuCount: 5,
      })

      if (result) await loadKUs()
      else setError('KU extraction failed')
    },
    [standardId, stdMeta, getPrompt, executeRun, loadKUs],
  )

  const handleVote = useCallback(
    async (koId: string, score: number) => {
      if (!user) return
      await upsertVote(koId, user.id, score, '')
      await loadKUs()
    },
    [user, upsertVote, loadKUs],
  )

  if (!stdMeta) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Standard not found</Alert>
      </Box>
    )
  }

  const subjectMeta = SUBJECT_META[stdMeta.subject]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Breadcrumb bar */}
      <Box
        sx={{
          px: 2,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography
          onClick={onNavigateToRoot}
          variant="caption"
          sx={{
            fontSize: 12,
            color: onNavigateToRoot ? 'text.secondary' : 'text.disabled',
            cursor: onNavigateToRoot ? 'pointer' : 'default',
            '&:hover': onNavigateToRoot ? { color: 'primary.main' } : {},
          }}
        >
          Standards
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 12 }}>
          ›
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 700, color: subjectMeta.color }}>
          {standardId}
        </Typography>
      </Box>

      {/* Standard header */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 2,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 900,
            color: subjectMeta.color,
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
            mb: 0.75,
          }}
        >
          {standardId}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7, fontSize: 13.5, mb: 0.75 }}>
          {stdMeta.std.text}
        </Typography>
        {stdMeta.domain && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
            {stdMeta.domain}
            {stdMeta.cluster ? ` › ${stdMeta.cluster}` : ''} · {stdMeta.subject} Grade {stdMeta.grade}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, p: 2, pb: 4, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          PASS_META.map((pass) => (
            <PassCard
              key={pass.id}
              pass={pass}
              standardId={standardId}
              standardText={stdMeta.std.text}
              subject={stdMeta.subject}
              grade={stdMeta.grade}
              kus={kusByPass[pass.id]}
              extractPrompt={getPrompt(pass.id, 'extract')}
              generatePrompt={pass.hasGenerate ? getPrompt(pass.id, 'generate') : undefined}
              canGenerate={caps.canGenerate}
              onGenerate={(provider, model) => handleGenerate(pass.id, provider, model)}
              onOpenKU={onSelectKU}
              userId={user?.id || ''}
              onVote={handleVote}
            />
          ))
        )}
      </Box>
    </Box>
  )
}
